import React, { useEffect, useState } from 'react';
import api from '../../api';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import StatusMessage from '../../components/admin/StatusMessage';
import { formatHtmlForEditing } from './emailTemplateUtils';

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateDraft, setTemplateDraft] = useState({ name: '', subject: '', body_html: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [confirmation, setConfirmation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await api.get('/api/admin/email/templates');
        setTemplates(response.data || []);
      } catch (error) {
        setTemplates([]);
      }
    };

    loadTemplates();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const startNewTemplate = () => {
    setSelectedTemplateId('');
    setTemplateDraft({ name: '', subject: '', body_html: '' });
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const selectedTemplate = templates.find((template) => template.id === templateId);
    if (!selectedTemplate) return;
    setTemplateDraft({
      name: selectedTemplate.name,
      subject: selectedTemplate.subject,
      body_html: formatHtmlForEditing(selectedTemplate.body_html),
    });
  };

  const formatTemplateBody = () => {
    setTemplateDraft((draft) => ({ ...draft, body_html: formatHtmlForEditing(draft.body_html) }));
  };

  const saveTemplate = async () => {
    if (!templateDraft.name.trim() || !templateDraft.subject.trim() || !templateDraft.body_html.trim()) {
      showMessage('error', 'Template name, subject, and body are required.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: templateDraft.name.trim(),
        subject: templateDraft.subject.trim(),
        body_html: templateDraft.body_html,
      };
      const response = selectedTemplateId
        ? await api.put(`/api/admin/email/templates/${selectedTemplateId}`, payload)
        : await api.post('/api/admin/email/templates', payload);
      const savedTemplate = response.data;
      setTemplates((current) => {
        const remaining = current.filter((template) => template.id !== savedTemplate.id);
        return [...remaining, savedTemplate].sort((a, b) => a.name.localeCompare(b.name));
      });
      setSelectedTemplateId(savedTemplate.id);
      setTemplateDraft({
        name: savedTemplate.name,
        subject: savedTemplate.subject,
        body_html: formatHtmlForEditing(savedTemplate.body_html),
      });
      showMessage('success', 'Email template saved.');
    } catch (error) {
      showMessage('error', `Failed to save template: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      await api.delete(`/api/admin/email/templates/${selectedTemplateId}`);
      setTemplates((current) => current.filter((template) => template.id !== selectedTemplateId));
      startNewTemplate();
      setConfirmation(null);
      showMessage('success', 'Email template deleted.');
    } catch (error) {
      setConfirmation(null);
      showMessage('error', `Failed to delete template: ${error.response?.data?.detail || error.message}`);
    }
  };

  return (
    <div className="admin-email-page">
      <AdminSectionHeader
        title="Manage Email Templates"
        subtitle="Create reusable HTML email templates for announcements and updates"
      />

      {message.text && <StatusMessage type={message.type} message={message.text} />}

      <section className="admin-section email-template-section">
        <div className="email-template-heading">
          <div>
            <h3>Email Body Templates</h3>
            <p className="field-helper">Choose a template to edit, or start a new one.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={startNewTemplate} disabled={isSaving}>
            <i className="fa-solid fa-plus"></i> New Template
          </button>
        </div>

        <label className="email-template-picker">
          Use Template
          <select
            value={selectedTemplateId}
            onChange={(event) => handleTemplateSelect(event.target.value)}
            disabled={isSaving}
            className="email-template-select"
          >
            <option value="">Start with a blank email</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
        </label>

        <div className="email-template-editor">
          <label>
            Template Name
            <input
              type="text"
              value={templateDraft.name}
              onChange={(event) => setTemplateDraft((draft) => ({ ...draft, name: event.target.value }))}
              placeholder="e.g. Monthly News"
              disabled={isSaving}
            />
          </label>
          <label>
            Template Subject
            <input
              type="text"
              value={templateDraft.subject}
              onChange={(event) => setTemplateDraft((draft) => ({ ...draft, subject: event.target.value }))}
              placeholder="Subject used when the template is selected"
              disabled={isSaving}
            />
          </label>
          <label>
            Template HTML Content
            <textarea
              value={templateDraft.body_html}
              onChange={(event) => setTemplateDraft((draft) => ({ ...draft, body_html: event.target.value }))}
              placeholder="Add the HTML structure and content for this template"
              className="email-template-html-editor"
              rows={18}
              disabled={isSaving}
            />
          </label>
          <p className="email-template-helper">
            Edit the text between the HTML tags. Keep the tags and inline styles in place so the email layout stays intact.
          </p>
          <div className="email-template-actions">
            <button type="button" className="btn btn-secondary" onClick={formatTemplateBody} disabled={isSaving}>
              <i className="fa-solid fa-align-left"></i> Format HTML
            </button>
            <button type="button" onClick={saveTemplate} disabled={isSaving}>
              <i className="fa-solid fa-floppy-disk"></i> Save Template
            </button>
            {selectedTemplateId && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setConfirmation({
                  title: 'Delete email template?',
                  message: 'This saved template will be permanently removed.',
                  onConfirm: deleteTemplate,
                })}
                disabled={isSaving}
              >
                <i className="fa-solid fa-trash"></i> Delete
              </button>
            )}
          </div>
        </div>
      </section>

      <ConfirmationModal
        isOpen={Boolean(confirmation)}
        title={confirmation?.title || 'Confirm'}
        message={confirmation?.message || ''}
        onConfirm={() => confirmation?.onConfirm?.()}
        onCancel={() => setConfirmation(null)}
        variant="warning"
      />
    </div>
  );
}
